import { abbrNumberFilter, swNumberFilter } from "app/filters/ngFilters";

/**
 * Adds "0" to the beginning of the given value.
 * May be usefull for time formatting.
 * @param value
 * @example ```
 *  prependZero(5) // "05"
 * ```
 */
export function prependZero(value: number) {
    const asString = String(value);

    if (asString.length === 1) {
        return `0${asString}`;
    }

    return asString;
}

/**
 * Formats given number using `swNumberFilter()`
 * @param n
 * @example ```
 *     formatNumberWithCommas(0) -> "0"
 *     formatNumberWithCommas(12435) -> "12,435"
 * ```
 */
export function formatNumberWithCommas(n: number | undefined): string {
    if (typeof n === "undefined") {
        return "";
    }

    if (n === 0) {
        return String(n);
    }

    return swNumberFilter()(n);
}

/**
 * Formats given number using `swNumberFilter()` with default limit of 100 mln
 * @example ```
 *     formatNumberWithLimit(0) -> "0"
 *     formatNumberWithLimit(12435) -> "12,435"
 *     formatNumberWithLimit(100000001) -> "100,000,000+"
 *     formatNumberWithLimit(1000, 100) -> "100+"
 * ```
 */
export const formatNumberWithLimit = (n: number | undefined, limit = 100000000): string => {
    if (typeof n === "undefined") {
        return "";
    }

    if (n > limit) {
        return `${formatNumberWithCommas(limit)}+`;
    }

    return formatNumberWithCommas(n);
};

const COMPACT_FORMAT_THRESHOLD = 1e4;

export const formatCompactNumber = (
    value: number | null | undefined,
    compactThreshold = COMPACT_FORMAT_THRESHOLD,
): string => {
    if (value === undefined || value === null || value === 0) {
        return "0";
    }

    return value >= compactThreshold ? (abbrNumberFilter()(value) as string) : formatNumberWithCommas(value);
};

const MAX_FRACTION_DIGITS = 2;
export const formatPercent = (
    fraction: number | null | undefined,
    maximumFractionDigits: number = MAX_FRACTION_DIGITS,
    locale: string = "en-US",
): string => {
    if (fraction == null) {
        return "";
    }

    return Intl.NumberFormat(locale, {
        style: "percent",
        maximumFractionDigits: maximumFractionDigits,
    }).format(fraction);
};
